// Parallel batch fetcher for efficient AniList API requests

import { batchFetchAnimeRelations } from './aniListService';

const BATCH_SIZE = 20;
const MAX_PARALLEL_WORKERS = 4;
const RATE_LIMIT_DELAY = 700; // ~700ms between batches to stay under 90 req/min
const MAX_RETRY_ATTEMPTS = 10; // Increased retries
const RETRY_DELAY_BASE = 1000; // Exponential backoff base
const MAX_BACKOFF_DELAY = 30000; // Cap at 30 seconds

/**
 * Split array into chunks of specified size
 * @param {Array} array - Array to chunk
 * @param {number} size - Chunk size
 * @returns {Array<Array>} Array of chunks
 */
const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

/**
 * Sleep for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch a single batch with retry logic - NEVER SKIPS
 * @param {Array<number>} mediaIds - Media IDs to fetch
 * @param {number} batchIndex - Index of this batch
 * @param {number} retryCount - Current retry attempt
 * @returns {Promise<Object>} Relations data
 */
const fetchBatchWithRetry = async (mediaIds, batchIndex, retryCount = 0) => {
    try {
        const relations = await batchFetchAnimeRelations(mediaIds);

        // Success! Log if it was after retries
        if (retryCount > 0) {
            console.log(`✅ Batch ${batchIndex} succeeded after ${retryCount} retries`);
        }

        return { success: true, data: relations, batchIndex };
    } catch (error) {
        // Calculate backoff delay with exponential increase, capped at MAX_BACKOFF_DELAY
        const backoffDelay = Math.min(
            RETRY_DELAY_BASE * Math.pow(2, retryCount),
            MAX_BACKOFF_DELAY
        );

        // Determine error type
        const isRateLimit = error.message.includes('Rate limit') ||
            error.response?.status === 429 ||
            error.message.includes('429');

        const isCORS = error.message.includes('CORS') ||
            error.message.includes('Network Error');

        // Log appropriate message
        if (isRateLimit) {
            console.warn(`⏸️  Batch ${batchIndex} rate limited (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS}), waiting ${backoffDelay}ms...`);
        } else if (isCORS) {
            console.warn(`🌐 Batch ${batchIndex} CORS/Network error (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS}), waiting ${backoffDelay}ms...`);
        } else {
            console.warn(`⚠️  Batch ${batchIndex} error (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS}): ${error.message}, waiting ${backoffDelay}ms...`);
        }

        // If we've exceeded max retries, use a longer delay but KEEP TRYING
        if (retryCount >= MAX_RETRY_ATTEMPTS) {
            console.error(`🔄 Batch ${batchIndex} exceeded ${MAX_RETRY_ATTEMPTS} retries, but will keep trying with ${MAX_BACKOFF_DELAY}ms delay...`);
            await sleep(MAX_BACKOFF_DELAY);
            // NEVER GIVE UP - keep retrying indefinitely
            return fetchBatchWithRetry(mediaIds, batchIndex, retryCount + 1);
        }

        // Wait and retry
        await sleep(backoffDelay);
        return fetchBatchWithRetry(mediaIds, batchIndex, retryCount + 1);
    }
};

/**
 * Worker function to process batches sequentially
 * @param {Array<Array>} batches - Batches assigned to this worker
 * @param {number} workerId - Worker ID for logging
 * @param {Function} onProgress - Progress callback
 * @param {Function} checkCancelled - Optional function to check if operation was cancelled
 * @returns {Promise<Object>} Combined results from all batches
 */
const processBatchesSequentially = async (batches, workerId, onProgress, checkCancelled = null) => {
    const results = {};
    let processedCount = 0;

    for (let i = 0; i < batches.length; i++) {
        // Check for cancellation before processing batch
        if (checkCancelled && checkCancelled()) {
            console.log(`🛑 Worker ${workerId} cancelled at batch ${i + 1}/${batches.length}`);
            break;
        }

        const batch = batches[i];
        const batchIndex = batch.index;
        const mediaIds = batch.data;

        // Fetch batch with retry
        const result = await fetchBatchWithRetry(mediaIds, batchIndex);

        if (result.success) {
            // Merge results
            Object.assign(results, result.data);
        }

        processedCount++;

        // Call progress callback
        if (onProgress) {
            onProgress({
                workerId,
                processedBatches: processedCount,
                totalBatches: batches.length,
                batchIndex,
                success: result.success
            });
        }

        // Rate limit delay (except for last batch)
        if (i < batches.length - 1) {
            await sleep(RATE_LIMIT_DELAY);
        }
    }

    return results;
};

/**
 * Distribute batches evenly across workers
 * @param {Array<Array>} batches - All batches
 * @param {number} numWorkers - Number of workers
 * @returns {Array<Array>} Batches distributed to each worker
 */
const distributeBatches = (batches, numWorkers) => {
    const workerBatches = Array.from({ length: numWorkers }, () => []);

    batches.forEach((batch, index) => {
        const workerIndex = index % numWorkers;
        workerBatches[workerIndex].push({
            index,
            data: batch
        });
    });

    return workerBatches;
};

/**
 * Fetch all relations in parallel using worker pool
 * @param {Array<number>} mediaIds - All media IDs to fetch
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<Object>} All relations data
 */
export const fetchAllRelationsParallel = async (mediaIds, onProgress = null) => {
    if (!mediaIds || mediaIds.length === 0) {
        return {};
    }

    // Split into batches
    const batches = chunkArray(mediaIds, BATCH_SIZE);
    const totalBatches = batches.length;

    console.log(`🚀 Starting parallel fetch: ${mediaIds.length} anime, ${totalBatches} batches, ${MAX_PARALLEL_WORKERS} workers`);

    // Distribute batches across workers
    const workerBatches = distributeBatches(batches, MAX_PARALLEL_WORKERS);

    // Track progress across all workers
    let totalProcessed = 0;
    const progressCallback = (workerProgress) => {
        totalProcessed++;

        if (onProgress) {
            onProgress({
                processedBatches: totalProcessed,
                totalBatches,
                processedAnime: totalProcessed * BATCH_SIZE,
                totalAnime: mediaIds.length,
                progress: Math.min(100, Math.round((totalProcessed / totalBatches) * 100)),
                workerId: workerProgress.workerId
            });
        }
    };

    // Start all workers in parallel
    const workerPromises = workerBatches.map((batches, workerId) =>
        processBatchesSequentially(batches, workerId, progressCallback)
    );

    // Wait for all workers to complete
    const workerResults = await Promise.all(workerPromises);

    // Merge all results
    const allRelations = {};
    workerResults.forEach(result => {
        Object.assign(allRelations, result);
    });

    console.log(`✅ Parallel fetch complete: ${Object.keys(allRelations).length} anime processed`);

    return allRelations;
};

/**
 * Estimate fetch time for given number of anime
 * @param {number} animeCount - Number of anime
 * @returns {number} Estimated time in seconds
 */
export const estimateFetchTime = (animeCount) => {
    const batches = Math.ceil(animeCount / BATCH_SIZE);
    const batchesPerWorker = Math.ceil(batches / MAX_PARALLEL_WORKERS);
    const timePerBatch = 1 + (RATE_LIMIT_DELAY / 1000); // API call + delay
    return Math.round(batchesPerWorker * timePerBatch);
};

/**
 * Fetch relations for specific anime IDs using parallel workers.
 * For small batches (< 4), uses sequential processing.
 * Supports cancellation via checkCancelled callback.
 * @param {Array<number>} mediaIds - Specific media IDs to fetch
 * @param {Function} onProgress - Progress callback (optional)
 * @param {Function} checkCancelled - Function to check if operation was cancelled (optional)
 * @returns {Promise<Object>} Relations data for the specified IDs
 */
export const fetchRelationsForIds = async (mediaIds, onProgress = null, checkCancelled = null) => {
    if (!mediaIds || mediaIds.length === 0) {
        return {};
    }

    // Split into batches
    const batches = chunkArray(mediaIds, BATCH_SIZE);
    const totalBatches = batches.length;

    // For very small updates (less than 4 batches), use sequential processing
    if (totalBatches < MAX_PARALLEL_WORKERS) {
        console.log(`🔄 Incremental fetch (sequential): ${mediaIds.length} anime, ${totalBatches} batches`);

        const allRelations = {};
        let processedBatches = 0;

        for (let i = 0; i < batches.length; i++) {
            // Check for cancellation before processing batch
            if (checkCancelled && checkCancelled()) {
                console.log(`🛑 Fetch cancelled at batch ${i + 1}/${totalBatches}`);
                break;
            }

            const batch = batches[i];
            const result = await fetchBatchWithRetry(batch, i);

            if (result.success) {
                Object.assign(allRelations, result.data);
            }

            processedBatches++;

            if (onProgress) {
                onProgress({
                    processedBatches,
                    totalBatches,
                    processedAnime: Math.min(processedBatches * BATCH_SIZE, mediaIds.length),
                    totalAnime: mediaIds.length,
                    progress: Math.round((processedBatches / totalBatches) * 100)
                });
            }

            if (i < batches.length - 1) {
                await sleep(RATE_LIMIT_DELAY);
            }
        }

        console.log(`✅ Incremental fetch complete: ${Object.keys(allRelations).length}/${mediaIds.length} anime fetched`);
        return allRelations;
    }

    // Use parallel workers for larger updates
    console.log(`🚀 Incremental fetch (parallel): ${mediaIds.length} anime, ${totalBatches} batches, ${MAX_PARALLEL_WORKERS} workers`);

    const workerBatches = distributeBatches(batches, MAX_PARALLEL_WORKERS);

    let totalProcessed = 0;
    const progressCallback = (workerProgress) => {
        totalProcessed++;
        if (onProgress) {
            onProgress({
                processedBatches: totalProcessed,
                totalBatches,
                processedAnime: totalProcessed * BATCH_SIZE,
                totalAnime: mediaIds.length,
                progress: Math.min(100, Math.round((totalProcessed / totalBatches) * 100)),
                workerId: workerProgress.workerId
            });
        }
    };

    const workerPromises = workerBatches.map((wBatches, workerId) =>
        processBatchesSequentially(wBatches, workerId, progressCallback, checkCancelled)
    );

    const workerResults = await Promise.all(workerPromises);

    const allRelations = {};
    workerResults.forEach(result => {
        Object.assign(allRelations, result);
    });

    console.log(`✅ Incremental fetch complete: ${Object.keys(allRelations).length}/${mediaIds.length} anime fetched`);
    return allRelations;
};
